import { AlertTriangle, Sparkles } from 'lucide-react'
import { Button } from '../ui'
import type { FloorPlanMetrics } from '../../lib/floorPlan/rfOptimizer'

type Props = {
  metrics: FloorPlanMetrics | null
  bandGHz: number
  placedApCount: number
  recommendationCount: number
  highPriorityCount: number
  optimizing: boolean
  onOpenRf: () => void
  onOptimize: () => void
}

function MetricLine({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'good' | 'weak' | 'interf'
}) {
  const colors = {
    good: 'bg-emerald-500',
    weak: 'bg-red-500',
    interf: 'bg-amber-500',
  }
  const text =
    tone === 'good'
      ? value >= 50
        ? 'text-emerald-400'
        : value >= 25
          ? 'text-amber-400'
          : 'text-red-400'
      : tone === 'weak'
        ? value <= 10
          ? 'text-emerald-400'
          : value <= 25
            ? 'text-amber-400'
            : 'text-red-400'
        : value <= 15
          ? 'text-emerald-400'
          : value <= 35
            ? 'text-amber-400'
            : 'text-red-400'

  return (
    <div>
      <div className="mb-0.5 flex items-center justify-between text-[10px]">
        <span className="text-white/70">{label}</span>
        <span className={`font-semibold tabular-nums ${text}`}>{Math.round(value)}%</span>
      </div>
      <div className="h-1 overflow-hidden rounded-full bg-white/15">
        <div
          className={`h-full rounded-full transition-[width] duration-300 ease-out ${colors[tone]}`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
    </div>
  )
}

export default function FloorPlanRfLiveMetrics({
  metrics,
  bandGHz,
  placedApCount,
  recommendationCount,
  highPriorityCount,
  optimizing,
  onOpenRf,
  onOptimize,
}: Props) {
  if (placedApCount === 0) return null

  return (
    <div className="pointer-events-auto absolute bottom-2 left-2 z-20 w-[min(200px,calc(100%-1rem))] rounded-lg border border-white/10 bg-black/75 p-2 shadow-xl backdrop-blur-md">
      <button
        type="button"
        onClick={onOpenRf}
        className="mb-2 flex w-full items-center justify-between text-left"
      >
        <span className="text-[10px] font-semibold uppercase tracking-wide text-white/80">
          RF live · {bandGHz} GHz
        </span>
        {recommendationCount > 0 && (
          <span
            className={`inline-flex items-center gap-0.5 rounded-full px-1.5 py-px text-[9px] font-bold ${
              highPriorityCount > 0 ? 'bg-red-500/90 text-white' : 'bg-amber-500/90 text-white'
            }`}
          >
            {highPriorityCount > 0 && <AlertTriangle size={9} />}
            {recommendationCount}
          </span>
        )}
      </button>

      {metrics ? (
        <div className="space-y-1.5">
          <MetricLine label="Good ≥ −65 dBm" value={metrics.goodPct} tone="good" />
          <MetricLine label="Weak &lt; −75 dBm" value={metrics.weakPct} tone="weak" />
          <MetricLine label="High interference" value={metrics.highInterfPct} tone="interf" />
        </div>
      ) : (
        <p className="text-[10px] text-white/60">Computing…</p>
      )}

      <Button
        variant="primary"
        size="sm"
        type="button"
        className="mt-2 h-7 w-full text-[11px]"
        disabled={optimizing}
        onClick={onOptimize}
      >
        <Sparkles size={12} />
        {optimizing ? 'Optimizing…' : 'Optimize'}
      </Button>
    </div>
  )
}
